/**
 * Daily Brief — the small pieces every widget draws with: the bar inside a
 * percent cell, the closing-range slider, Market Performance's 1D-over-2D
 * gauge, the ▲/▼ point change, and the chips in a title bar.
 *
 * Colours are the theme's tokens only (`docs/features/theming.md`); the mock's
 * greens and reds (`docs/daily-brief-mock.html`) are `--qf-positive` and
 * `--qf-negative` here, mixed down for the fills.
 */
import type { CSSProperties, ReactNode } from "react";
import { barWidth, rangeTone } from "../../lib/brief-board";

/** `+1.23%`, `—` when the server had no denominator. */
export function pct(v: number | null | undefined, digits = 2): string {
  if (typeof v !== "number" || !Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

export function price(v: number | null | undefined): string {
  return typeof v === "number" && Number.isFinite(v) ? v.toFixed(2) : "—";
}

/** A number's colour by its sign. */
export function signColor(v: number | null | undefined): string {
  if (typeof v !== "number" || !Number.isFinite(v) || v === 0) return "var(--qf-text-muted)";
  return v > 0 ? "var(--qf-positive)" : "var(--qf-negative)";
}

const TONE: Record<"up" | "down" | "flat", string> = {
  up: "var(--qf-positive)",
  down: "var(--qf-negative)",
  flat: "var(--qf-text-secondary)",
};

/**
 * A percent cell with its bar inside (decision 5): zero in the middle, the
 * column's largest |value| filling its half, green to the right, red to the left.
 */
export function BarCell({ value, max }: { value: number | null | undefined; max: number }) {
  const w = barWidth(value, max);
  const side = typeof value === "number" && value < 0 ? "left" : "right";
  return (
    <td style={NUM_BAR}>
      <div style={BAR_ROW}>
        <span className={BAR_TRACK_CLASS} style={BAR_TRACK}>
          {w > 0 && (
            <b
              data-side={side}
              style={{ ...(side === "right" ? BAR_UP : BAR_DOWN), width: `${w}%` }}
            />
          )}
        </span>
        <span style={{ color: signColor(value), minWidth: 48, textAlign: "right" }}>{pct(value)}</span>
      </div>
    </td>
  );
}

/** Where the close sat in the bar's range, 0–100, as a tick on a track and its number. */
export function RangeSlider({ value }: { value: number | null | undefined }) {
  const ok = typeof value === "number" && Number.isFinite(value);
  const color = TONE[rangeTone(value)];
  const at = ok ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={RANGE_TRACK} aria-hidden>
        {ok && <i style={{ ...RANGE_TICK, left: `calc(${at}% - 1.5px)`, background: color }} />}
      </span>
      <span style={{ color: ok ? color : "var(--qf-text-muted)", width: 26, textAlign: "right" }}>
        {ok ? Math.round(value) : "—"}
      </span>
    </span>
  );
}

/** Market Performance's gauge: today's bar on top (green from 50 up), the 2D bar thinner beneath. */
export function DualGauge({ d1, d2 }: { d1: number | null; d2: number | null }) {
  const clamp = (v: number | null) => (v === null ? 0 : Math.max(0, Math.min(100, v)));
  return (
    <span style={{ display: "block", width: "100%" }} aria-hidden>
      <div style={{ ...GAUGE_TRACK, height: 12 }}>
        <b
          style={{
            ...GAUGE_FILL,
            width: `${clamp(d1)}%`,
            background: d1 !== null && d1 >= 50 ? "var(--qf-positive)" : "var(--qf-negative)",
          }}
        />
        <span style={GAUGE_MID} />
      </div>
      <div style={{ ...GAUGE_TRACK, height: 6 }}>
        <b style={{ ...GAUGE_FILL, width: `${clamp(d2)}%`, background: PREV }} />
      </div>
    </span>
  );
}

/** Today's value against the 2D one, in points: ▲ 9, ▼ 4, or = . */
export function PointDelta({ a, b }: { a: number | null; b: number | null }) {
  if (a === null || b === null) return <span style={MUTED}>—</span>;
  const d = Math.round(a) - Math.round(b);
  if (d === 0) return <span style={MUTED}>=</span>;
  return <span style={{ color: signColor(d) }}>{d > 0 ? "▲" : "▼"} {Math.abs(d)}</span>;
}

/**
 * A chip: a toggle when `on` is given (announced as pressed or not), else a
 * plain action — `primary` only lends an action the toggle's lit look, so
 * "Add" is not read out as "toggle button, pressed".
 */
export function Chip({
  on, primary, onClick, children, title, label, disabled, menu,
}: {
  on?: boolean;
  primary?: boolean;
  onClick?: () => void;
  children: ReactNode;
  title?: string;
  /** The accessible name, when the content is a glyph ("▶") rather than words. */
  label?: string;
  disabled?: boolean;
  /** The chip opens a menu: whether it is open now. */
  menu?: boolean;
}) {
  return (
    <button
      type="button"
      className="qf-brief-nodrag"
      aria-pressed={on}
      aria-haspopup={menu === undefined ? undefined : "menu"}
      aria-expanded={menu}
      title={title}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={on || primary ? { ...CHIP, ...CHIP_ON } : CHIP}
    >
      {children}
    </button>
  );
}

/** The 2D colour the mock calls `--prev`: a quiet light grey beside the 1D one. */
export const PREV = "var(--qf-text-secondary)";

export const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
export const TABLE: CSSProperties = {
  borderCollapse: "collapse", width: "100%", fontVariantNumeric: "tabular-nums", fontSize: 13,
};
// Tight on purpose (owner, 2026-10-18: "fit the size so no horizontal scroll
// bar"): the name column gives way first (`PerfTable`'s text cell), then the
// bars go below `BAR_HIDE_BELOW` px of widget width (`BAR_HIDE_CSS`).
export const TH: CSSProperties = {
  padding: "3px 5px", borderBottom: "1px solid var(--qf-border)", whiteSpace: "nowrap", textAlign: "left",
  fontWeight: 600, color: "var(--qf-text-muted)", fontSize: 12, textTransform: "uppercase",
  position: "sticky", top: 0, background: "var(--qf-background)", zIndex: 1,
};
export const TH_NUM: CSSProperties = { ...TH, textAlign: "right" };
export const TD: CSSProperties = {
  padding: "3px 5px", borderBottom: "1px solid color-mix(in srgb, var(--qf-border) 55%, transparent)",
  whiteSpace: "nowrap",
};
export const TD_NUM: CSSProperties = { ...TD, textAlign: "right" };
const NUM_BAR: CSSProperties = { ...TD_NUM, paddingLeft: 2 };
const BAR_ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" };
const BAR_TRACK_CLASS = "qf-brief-bar";
/** Widget width under which the bars inside the percent cells are dropped and only the numbers stay. */
export const BAR_HIDE_BELOW = 760;
/** A container query on the widget body (`WidgetCard`'s `containerType`), so it follows the widget, not the window. */
export const BAR_HIDE_CSS = `@container (max-width: ${BAR_HIDE_BELOW}px) { .${BAR_TRACK_CLASS} { display: none; } }`;
const BAR_TRACK: CSSProperties = {
  position: "relative", flex: "0 0 40px", height: 13,
  // The zero line, down the middle.
  backgroundImage: "linear-gradient(var(--qf-border), var(--qf-border))",
  backgroundSize: "1px 100%", backgroundPosition: "center", backgroundRepeat: "no-repeat",
};
const BAR_UP: CSSProperties = {
  position: "absolute", top: 0, bottom: 0, left: "50%",
  background: "linear-gradient(90deg, color-mix(in srgb, var(--qf-positive) 15%, transparent), color-mix(in srgb, var(--qf-positive) 60%, transparent))",
  border: "1px solid var(--qf-positive)", borderLeft: 0,
};
const BAR_DOWN: CSSProperties = {
  position: "absolute", top: 0, bottom: 0, right: "50%",
  background: "linear-gradient(270deg, color-mix(in srgb, var(--qf-negative) 15%, transparent), color-mix(in srgb, var(--qf-negative) 60%, transparent))",
  border: "1px solid var(--qf-negative)", borderRight: 0,
};
const TRACK = "color-mix(in srgb, var(--qf-border) 80%, transparent)";
const RANGE_TRACK: CSSProperties = {
  position: "relative", display: "inline-block", width: 64, height: 4, background: TRACK, borderRadius: 2,
};
const RANGE_TICK: CSSProperties = { position: "absolute", top: -4, width: 3, height: 12, borderRadius: 1 };
const GAUGE_TRACK: CSSProperties = { position: "relative", background: TRACK, borderRadius: 2, margin: "2px 0" };
const GAUGE_FILL: CSSProperties = { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 2 };
const GAUGE_MID: CSSProperties = {
  position: "absolute", left: "50%", top: -2, bottom: -2, width: 1, background: "var(--qf-text-muted)",
};
const CHIP: CSSProperties = {
  // Longhands, so `CHIP_ON`'s colour can swap in without React clearing a shorthand.
  borderWidth: 1, borderStyle: "solid", borderColor: "var(--qf-border)",
  borderRadius: 5, padding: "1px 8px", background: "var(--qf-surface-alt)",
  color: "var(--qf-text)", cursor: "pointer", fontSize: 13, fontWeight: 400, whiteSpace: "nowrap",
};
const CHIP_ON: CSSProperties = {
  borderColor: "var(--qf-accent)", background: "var(--qf-selection)", color: "var(--qf-accent)",
};
